/**
 * The sales agent's "day" resets at a LOCAL time in a real timezone — default
 * 09:00 Europe/Riga (the start of the working day in Latvia). These MUST mirror
 * the agent's `daily_budget_reset_tz` / `daily_budget_reset_hour_local`
 * (xop-agents `app/config.py`) so the dashboard's "today" window lines up with
 * the agent's budget/search day. Anchored to a timezone (NOT a fixed UTC hour)
 * so it tracks DST automatically — 09:00 Riga is 06:00 UTC in summer, 07:00 UTC
 * in winter. If you change the agent's reset env, update these to match.
 */
export const BUDGET_RESET_TZ = "Europe/Riga";
export const BUDGET_RESET_HOUR = 9;

/** Wall-clock fields of `instant` as seen in `tz`. */
function tzParts(instant: Date, tz: string): {
  y: number; mo: number; d: number; h: number; mi: number; s: number;
} {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) p[part.type] = part.value;
  const h = Number(p.hour);
  return {
    y: Number(p.year),
    mo: Number(p.month),
    d: Number(p.day),
    h: h === 24 ? 0 : h, // some engines render midnight as "24"
    mi: Number(p.minute),
    s: Number(p.second),
  };
}

/** The UTC instant (ms) of a wall-clock time in `tz`. Single-pass: the zone
 * offset is read at the target instant, which is exact for an on-the-hour
 * boundary away from the DST transition itself (EU flips at 03:00/04:00, not
 * 09:00). */
function zonedWallToUtcMs(
  y: number, mo: number, d: number, h: number, tz: string,
): number {
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, 0, 0);
  const shown = tzParts(new Date(wallAsUtc), tz);
  const shownAsUtc = Date.UTC(shown.y, shown.mo - 1, shown.d, shown.h, shown.mi, shown.s);
  const offset = shownAsUtc - wallAsUtc; // how far the zone is ahead of UTC
  return wallAsUtc - offset;
}

/** A calendar date (the agent day's Y/M/D in {@link BUDGET_RESET_TZ}). */
interface DayYmd {
  y: number;
  mo: number;
  d: number;
}

/** The agent day `now` falls in: today's date in Riga once past the reset
 * hour, else yesterday's. */
function agentDay(now: Date): DayYmd {
  const p = tzParts(now, BUDGET_RESET_TZ);
  if (p.h >= BUDGET_RESET_HOUR) return { y: p.y, mo: p.mo, d: p.d };
  // Before today's reset → still yesterday's window. Step back one LOCAL day
  // (via a UTC scratch date — we only need Y/M/D arithmetic here).
  return addDays({ y: p.y, mo: p.mo, d: p.d }, -1);
}

/** Pure calendar arithmetic (month/year roll-over handled by Date.UTC). */
function addDays(day: DayYmd, n: number): DayYmd {
  const t = new Date(Date.UTC(day.y, day.mo - 1, day.d + n));
  return { y: t.getUTCFullYear(), mo: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/** The instant an agent day begins — {@link BUDGET_RESET_HOUR}:00 Riga on it. */
function dayStartIso(day: DayYmd): string {
  return new Date(zonedWallToUtcMs(day.y, day.mo, day.d, BUDGET_RESET_HOUR, BUDGET_RESET_TZ)).toISOString();
}

/**
 * Start of the current agent "day" as an ISO instant — the most recent
 * {@link BUDGET_RESET_HOUR}:00 in {@link BUDGET_RESET_TZ} (today if we're past
 * it, else yesterday). The lower bound for "added today" counts on the Sales ▸
 * Today tab and the dashboard pulse headline, so they count the SAME day the
 * agent uses (its daily budget, Economics `leads_today`, and this all key off
 * the one reset boundary).
 *
 * Timezone-independent: a pure function of the absolute instant, NOT the
 * viewer's local timezone — so every operator worldwide sends the identical
 * `created_after` and counts the identical leads (the "EU sees 10, US sees 6 of
 * the same leads" fix). Recomputed per call so a surface left open across the
 * boundary rolls over on its own.
 */
export function startOfAgentDayIso(now: Date = new Date()): string {
  return dayStartIso(agentDay(now));
}

/** The Leads page's recency windows. */
export type AgentWindow = "today" | "week" | "last7" | "month" | "last_month";

/**
 * A recency window as `[after, before)` instants, built from the SAME day
 * boundary as "Today" (09:00 Riga) so every window agrees with it: a lead in
 * Today is always in This week, Last 7 days and This month too.
 *
 *   today      — since today's reset
 *   week       — since Monday's reset (ISO week, Monday first)
 *   last7      — the last 7 agent days, today included
 *   month      — since the reset on the 1st of this month
 *   last_month — the 1st of last month up to (not including) the 1st of this
 *
 * Only `last_month` is closed; the rest run to now, so `before` is absent.
 */
export function agentWindowIso(
  kind: AgentWindow,
  now: Date = new Date(),
): { after: string; before?: string } {
  const day = agentDay(now);
  switch (kind) {
    case "today":
      return { after: dayStartIso(day) };
    case "week": {
      const dow = new Date(Date.UTC(day.y, day.mo - 1, day.d)).getUTCDay(); // 0 = Sunday
      return { after: dayStartIso(addDays(day, -((dow + 6) % 7))) };
    }
    case "last7":
      return { after: dayStartIso(addDays(day, -6)) };
    case "month":
      return { after: dayStartIso({ y: day.y, mo: day.mo, d: 1 }) };
    case "last_month": {
      const firstOfThis = { y: day.y, mo: day.mo, d: 1 };
      const firstOfLast = day.mo === 1 ? { y: day.y - 1, mo: 12, d: 1 } : { y: day.y, mo: day.mo - 1, d: 1 };
      return { after: dayStartIso(firstOfLast), before: dayStartIso(firstOfThis) };
    }
  }
}
